import { forwardRef, HTMLProps } from 'react'

import * as ToolbarPrimitive from '@radix-ui/react-toolbar'
import { twMerge } from 'tailwind-merge'

import Tooltip from '../Tooltip/Tooltip'

type ToolbarBaseProps = {
  children: React.ReactNode
  className?: string
}
const ToolbarBase = ({ children, className }: ToolbarBaseProps) => {
  return (
    <ToolbarPrimitive.Root
      className={twMerge(
        'relative flex h-fit w-fit max-w-[90%] flex-wrap items-center justify-center gap-1 rounded-lg border border-neutral-300 bg-white p-2 shadow-md',
        className
      )}
    >
      {children}
    </ToolbarPrimitive.Root>
  )
}

type ToolbarToggleGroupProps<TValue extends string> = {
  children: React.ReactNode
} & (
  | {
      type: 'single'
      onValueChange: (value: TValue | undefined) => void
      value: TValue | undefined
    }
  | { type: 'multiple'; onValueChange: (value: TValue[]) => void; value: TValue[] }
)
const ToolbarToggleGroup = <TValue extends string>({
  children,
  onValueChange,
  type,
  value,
}: ToolbarToggleGroupProps<TValue>) => {
  return (
    <ToolbarPrimitive.ToggleGroup
      {...(type === 'single'
        ? {
            type: 'single',
            onValueChange: (v: string | undefined) => onValueChange(v as TValue | undefined),
            value,
          }
        : {
            type: 'multiple',
            onValueChange: (v: string[]) => onValueChange(v as TValue[]),
            value,
          })}
      className="flex items-center gap-1"
    >
      {children}
    </ToolbarPrimitive.ToggleGroup>
  )
}

type ToolbarToggleItemProps<TValue extends string> = {
  children: React.ReactNode
  tooltip?: string
  value: TValue
}

const ToolbarToggleItem = <TValue extends string>({
  children,
  tooltip,
  value,
}: ToolbarToggleItemProps<TValue>) => {
  return (
    <Tooltip content={tooltip}>
      <div>
        <ToolbarPrimitive.ToggleItem
          className={`min-h-[32px] min-w-[32px] cursor-pointer rounded-md border border-transparent px-2 transition-colors duration-200 hover:bg-neutral-100 focus-visible:border-sky-800 active:border-sky-800 data-[state="on"]:bg-sky-500 data-[state="on"]:text-white`}
          value={value}
        >
          {children}
        </ToolbarPrimitive.ToggleItem>
      </div>
    </Tooltip>
  )
}

const ToolbarSeparator = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>((props, ref) => (
  <ToolbarPrimitive.Separator
    ref={ref}
    {...props}
    className={twMerge('mx-1 h-[26px] border-l border-neutral-200', props.className)}
  />
))

export default Object.assign(ToolbarBase, {
  Separator: ToolbarSeparator,
  ToggleGroup: ToolbarToggleGroup,
  ToggleItem: ToolbarToggleItem,
})
